import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useFeatureRegistration } from '@/contexts/FeatureRegistryContext';
import { useEntitlements } from '@/hooks/useEntitlements';
import { WATCH_LIMIT_EVENT } from '@/lib/watchLimit';

export const FEATURE_META = {
  id: 'watch-limit-upgrade',
  label: 'Watch limit reached',
  description: 'Shown when a plan has used all its watches or saved searches; explains Buyer Pro gives unlimited.',
  triggerType: 'dialog' as const,
  personas: ['buyer', 'buyer_seller', 'seller'] as ('buyer' | 'buyer_seller' | 'seller')[],
};

/** One app-wide sheet: a buyer at their allowance sees a friendly ceiling with a way past it, never an error. */
const WatchLimitDialog = () => {
  useFeatureRegistration(FEATURE_META);
  const navigate = useNavigate();
  const ent = useEntitlements();
  const [kind, setKind] = useState<'watch' | 'search' | null>(null);

  useEffect(() => {
    const onLimit = (e: Event) => setKind((e as CustomEvent).detail?.kind ?? 'watch');
    window.addEventListener(WATCH_LIMIT_EVENT, onLimit);
    return () => window.removeEventListener(WATCH_LIMIT_EVENT, onLimit);
  }, []);

  const limit = ent.limitOf('alerts.watchlists') ?? 3;
  const noun = kind === 'search' ? 'saved searches' : 'watches';
  const go = (path: string) => { setKind(null); navigate(path); };

  return (
    <Dialog open={kind !== null} onOpenChange={(o) => !o && setKind(null)}>
      <DialogContent className="sm:max-w-md text-center">
        <div className="mx-auto mb-1 flex circle-12 items-center justify-center rounded-full bg-primary/10">
          <Sparkles className="h-6 w-6 text-primary" />
        </div>
        <DialogHeader className="sm:text-center">
          <DialogTitle className="text-xl">You're onto something good</DialogTitle>
          <DialogDescription>
            Your plan includes {limit} {noun}, and you've used them all. Buyer Pro lets you watch as many listings,
            suppliers and searches as you like.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-2 text-left text-sm mx-auto">
          {['Unlimited watches and saved searches', 'Price drops and fresh drops the moment they land', 'Keep everything you already follow'].map((t) => (
            <li key={t} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-primary shrink-0" />{t}</li>
          ))}
        </ul>
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button className="w-full" onClick={() => go('/billing')}>See Buyer Pro</Button>
          <Button variant="quiet" className="w-full" onClick={() => go('/watchlist')}>Manage what I follow</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WatchLimitDialog;
